// src/pages/modules/RequestIssuancePage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchRequests,
  createRequest,
  updateRequest,
  deleteRequest,
  markInProgress,
  approveRequest,
  rejectRequest,
  clearError,
  acknowledgeRequest,
  setPendingRequestAck,
  clearPendingRequestAck,
} from 'store/slices/requestIssuanceSlice';
import { fetchEmployees } from 'store/slices/employeeSlice';
import { fetchStocks } from 'store/slices/stockSlice';
import { fetchRequestLogs } from "store/slices/requestLogSlice";
import {
  fetchAvailableInventories,
  fetchIssuanceEmployeesIT,
  fetchIssuanceEmployeesNonIT,
  fetchITStock,
  fetchNonITStock,
  restoreRole,
  fetchInventoryCategories,
} from 'store/slices/commonSlice';
import profileApi from 'api/profileApi';

import { Pencil, Trash2, CheckCircle, XCircle, MoreHorizontal, Loader2, Plus, PlayCircle, MoreVertical, History, Clock, PackageCheck, Eye, ChevronsUpDown, Check } from 'lucide-react';
import DataTable from 'components/common/DataTable';
import PageHeader from 'components/common/PageHeader';
import FormField from 'components/common/FormField';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from 'components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from 'components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';

import { usePermissions } from 'hooks/usePermissions';
import usePermissionSocket from 'hooks/usePermissionSocket';
import useIssuanceSocket from 'hooks/useIssuanceSocket';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Switch } from 'components/ui/switch';
import { fetchEmployeeDropdown } from 'store/slices/commonSlice';
import { ScrollArea } from 'components/ui/scroll-area';
import { Card } from 'components/ui/card';
import { cn } from 'lib/utils';
import { format, parseISO } from 'date-fns';
import { fetchActivityLogs, clearError as clearActivityLogError } from 'store/slices/activityLogSlice';
import LogDataDisplay from 'components/common/LogDataDisplay';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { canManageEverything } from 'utlis/authUtils';
import { formatDateTime } from 'utlis/formateDateTime';

const BEHALF_OF_OPTIONS = [
  { value: 'self', label: 'Self' },
  { value: 'new_joined', label: 'New Joined' },
  { value: 'management', label: 'Management' },
  { value: 'housekeeping', label: 'Housekeeping' },
];

const RequestIssuancePage = () => {
  useIssuanceSocket();
  usePermissionSocket();

  const isSuperAdmin = canManageEverything();

  const dispatch = useDispatch();
  const { requests, pagination, isLoading, error, pendingAcknowledgement } = useSelector((s) => s.requestIssuance);
  const employees = useSelector((state) => state.common.employees.items);

  // Inventory Categories and Subcategories
  const inventoryCategories = useSelector((state) => state.common.inventoryCategories.items);

  // NEW: Stock By Category State
  const itStockByCategory = useSelector((state) => state.common.itStockByCategory.byCategory);
  const nonITStockByCategory = useSelector((state) => state.common.nonITStockByCategory.byCategory);

  const {
    issuanceEmployeesIT,
    issuanceEmployeesNonIT,
    // itStock, // Not strictly needed if using ByCategory, but keeping in case
    // nonITStock,
  } = useSelector((state) => state.common);
  const { stocks = [] } = useSelector((s) => s.stock);

  const { logs: requestLogs, isLoading: logsLoading, error: logsError } = useSelector((s) => s.activityLog);

  const { canCreate, canUpdate, canDelete, canRead } = usePermissions('request');
  const [canApproveOrReject, setCanApproveOrReject] = useState(false);

  // Logged in user
  const loggedInUser = JSON.parse(localStorage.getItem("employee") || "{}");
  const loggedInId = loggedInUser?.id;

  const isRequester = (request) => {
    return request?.created_by === loggedInId || request?.request_by === loggedInId;
  };

  const [isITRelated, setIsITRelated] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [currentReq, setCurrentReq] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // const [approveQuantities, setApproveQuantities] = useState({});
  const [approveQuantities, setApproveQuantities] = useState({}); // Non-IT: quantity
  const [approveSelectedInventories, setApproveSelectedInventories] = useState({}); // IT: array of inventory IDs
  // const [availableInventories, setAvailableInventories] = useState({}); // From API
  // const [loadingAvailable, setLoadingAvailable] = useState(false);

  const [inProgressOpen, setInProgressOpen] = useState(false);
  const [expectedCompletion, setExpectedCompletion] = useState('');
  const [expectedCompletionError, setExpectedCompletionError] = useState('');


  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const [form, setForm] = useState({
    request_to: '',
    behalf_of: 'self',
    behalf_of_employee: '',
    reason: '',
    items: [],
  });

  const [formErrors, setFormErrors] = useState({});
  const [ackNotes, setAckNotes] = useState("");

  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  // Reset error when dialog closes
  useEffect(() => {
    if (!inProgressOpen) {
      setExpectedCompletion('');
      setExpectedCompletionError('');
    }
  }, [inProgressOpen]);

  // Load Inventory Categories on mount
  useEffect(() => {
    dispatch(fetchInventoryCategories());
  }, [dispatch]);

  // Load profile to check can_manage_issuance_it / non_it
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await profileApi.getMyProfile();
        setCanApproveOrReject(profile?.can_manage_issuance_it || profile?.can_manage_issuance_non_it);
      } catch (err) {
        console.error('Failed to load profile for issuance permissions', err);
      }
    };
    loadProfile();
  }, []);

  const refetch = useCallback(() => {
    dispatch(fetchRequests({ page, pageSize, search: searchTerm }));
  }, [dispatch, page, pageSize, searchTerm]);

  useEffect(() => { refetch(); }, [refetch]);
  useEffect(() => { dispatch(fetchStocks({ page: 1, pageSize: 1000 })); }, [dispatch]);

  useEffect(() => {
    dispatch(fetchEmployeeDropdown({ exclude_self: true }));
  }, [dispatch]);

  useEffect(() => {
    if (isITRelated) {
      dispatch(fetchIssuanceEmployeesIT());
      // Removed global fetchITStock() here as we fetch by category now
    } else {
      dispatch(fetchIssuanceEmployeesNonIT());
      // Removed global fetchNonITStock() here
    }
  }, [dispatch, isITRelated]);

  // Global error toast (from Redux)
  useEffect(() => {
    if (error) {
      const msg = typeof error === 'string' ? error : error.detail || error.error || 'An error occurred';
      toast.error(msg);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Check if current user is the requester AND request is approved but not acknowledged
  const needsAcknowledgement = (req) => {
    return (
      req.created_by === loggedInId &&
      req.status === "ack_pending" &&
      !req.is_acknowledged
    );
  };


  // Parse backend validation errors → convert to formErrors
  const parseBackendErrors = (errData) => {
    const errors = {};

    if (!errData) return errors;

    // Handle nested items errors like: items[1].request_quantity
    if (errData.items && Array.isArray(errData.items)) {
      errData.items.forEach((itemError, idx) => {
        if (typeof itemError === 'object') {
          Object.keys(itemError).forEach((field) => {
            const messages = itemError[field];
            if (Array.isArray(messages)) {
              errors[`item_${idx}_${field}`] = messages.join(', ');
            } else {
              errors[`item_${idx}_${field}`] = String(messages);
            }
          });
        }
      });
    }

    // Handle top-level fields
    Object.keys(errData).forEach((key) => {
      if (key !== 'items' && key !== 'detail' && key !== 'error') {
        const val = errData[key];
        errors[key] = Array.isArray(val) ? val.join(', ') : String(val);
      }
    });

    return errors;
  };

  const openHistory = async (request) => {
    setSelectedRequestId(request.id);
    setHistoryOpen(true);

    // Now using general activity log API with resource=request & object_id
    dispatch(fetchActivityLogs({
      page: 1,
      page_size: 50000,
      resource: 'request',
      object_id: request.id,
    }));
  };

  // If you show logs error somewhere
  useEffect(() => {
    if (logsError) {
      toast.error(logsError);
      dispatch(clearActivityLogError());
    }
  }, [logsError, dispatch]);

  const validateForm = () => {
    const errors = {};
    if (!form.request_to) errors.request_to = 'Please select who to request from';
    if ((form.behalf_of === 'new_joined' || form.behalf_of === 'management') && !form.behalf_of_employee) {
      errors.behalf_of_employee = 'Please select an employee';
    }
    if (!form.reason.trim()) errors.reason = 'Reason is required';
    if (form.items.length === 0) {
      errors.items = 'At least one item is required';
    } else {
      form.items.forEach((item, idx) => {
        if (!item.is_other && !item.inventory) {
          errors[`item_${idx}_inventory`] = 'Select an inventory item or mark as Other';
        }
        if (item.is_other && !item.other_reason?.trim()) {
          errors[`item_${idx}_other_reason`] = 'Describe the other item';
        }
        if (!item.request_quantity || item.request_quantity < 1) {
          errors[`item_${idx}_quantity`] = 'Quantity must be at least 1';
        }
      });
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openDialog = (req = null) => {
    if (req && !canUpdate) return toast.error("You don't have permission to edit");
    if (!req && !canCreate) return toast.error("You don't have permission to create");

    if (req) {
      setCurrentReq(req);

      // Determine IT/Non-IT based on the first non-other item's inventory_details.is_it
      // If is_it is true → IT (true), otherwise → Non-IT (false)
      const firstInventoryItem = req.items.find(i => !i.is_other && i.inventory_details);
      const isRequestIT = firstInventoryItem?.inventory_details?.is_it === true;

      // Set the toggle state
      setIsITRelated(isRequestIT);

      const formItems = req.items.map(i => ({
        id: i.id || undefined,
        category: i.inventory_details?.category_id || '',
        inventory: i.inventory_details?.id || '',
        request_quantity: i.request_quantity,
        is_other: i.is_other,
        other_reason: i.other_reason || '',
      }));

      setForm({
        request_to: req.request_to,
        behalf_of: req.behalf_of,
        behalf_of_employee: req.behalf_of_employee || '',
        reason: req.reason || '',
        items: formItems,
      });

      // Fetch stock for all categories that are already selected
      // Use the local isRequestIT variable since state update is async
      formItems.forEach((item, index) => {
        if (!item.is_other && item.category) {
          console.log(`Item ${index}: requesting stock for category → ${item.category}`);
          if (isRequestIT) {
            dispatch(fetchITStock(item.category));
          } else {
            dispatch(fetchNonITStock(item.category));
          }
        }
      });
    } else {
      // New request - default to IT (true) or Non-IT (false) as per your preference
      setCurrentReq(null);
      setIsITRelated(true); // Default to IT for new requests
      setForm({
        request_to: '',
        behalf_of: 'self',
        behalf_of_employee: '',
        reason: '',
        items: []
      });
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const addItemRow = () => {
    setForm(p => ({
      ...p,
      items: [...p.items, { inventory: '', category: '', request_quantity: '', is_other: false, other_reason: '' }],
    }));
  };

  const removeItemRow = (idx) => {
    setForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));
    setFormErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach(k => {
        if (k.startsWith(`item_${idx}_`)) delete newErrors[k];
      });
      return newErrors;
    });
  };

  const updateItem = (idx, field, value) => {
    setForm(p => ({
      ...p,
      items: p.items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)),
    }));

    // Clear related error
    setFormErrors(prev => ({ ...prev, [`item_${idx}_${field}`]: undefined }));

    // If category changes, fetch stock and clear current selection
    if (field === 'category' && value) {
      if (isITRelated) {
        dispatch(fetchITStock(value));
      } else {
        dispatch(fetchNonITStock(value));
      }
      setForm(p => ({
        ...p,
        items: p.items.map((it, i) => (i === idx ? { ...it, inventory: '' } : it))
      }));
    }
  };

  const handleMarkInProgress = async () => {
    try {
      await dispatch(markInProgress(currentReq.id)).unwrap();
      toast.success('Request marked as Ready (In Progress)');
      setInProgressOpen(false);
      refetch();
    } catch (err) {
      const message = err?.detail || err?.message || 'Failed to mark as ready';
      toast.error(message);
    }
  };

  const handleSubmit = async () => {
    setFormErrors({});

    if (!validateForm()) return;

    const payload = {
      request_to: form.request_to,
      behalf_of: form.behalf_of,
      ...((form.behalf_of === 'new_joined' || form.behalf_of === 'management') && { behalf_of_employee: form.behalf_of_employee }),
      reason: form.reason.trim(),
      items: form.items
        .filter(i => i.request_quantity > 0)
        .map(i => ({
          ...(i.id ? { id: i.id } : {}),
          inventory: i.is_other ? undefined : i.inventory,
          request_quantity: Number(i.request_quantity),
          is_other: i.is_other,
          other_reason: i.is_other ? i.other_reason.trim() : undefined,
        })),
    };

    try {
      if (currentReq) {
        await dispatch(updateRequest({ id: currentReq.id, ...payload })).unwrap();
        toast.success('Request updated successfully');
      } else {
        await dispatch(createRequest(payload)).unwrap();
        toast.success('Request created successfully');
      }
      setDialogOpen(false);
      refetch();
    } catch (err) {
      console.log("errrr", err)
      // Parse backend validation errors
      const backendErrors = parseBackendErrors(err);
      if (Object.keys(backendErrors).length > 0) {
        setFormErrors(backendErrors);
        toast.error(err.items[0]?.request_quantity || 'Please fix the errors below');
      }
    }
  };

  const handleDelete = async () => {
    if (!canDelete) {
      toast.error("You don't have permission to delete");
      return;
    }

    try {
      await dispatch(deleteRequest(currentReq.id)).unwrap();
      toast.success('Request deleted successfully');
      setDeleteOpen(false);
      refetch();
    } catch (err) {
      const message =
        err?.detail ||
        err?.error ||
        err?.message ||
        (typeof err === 'string' ? err : 'Cannot delete this request');

      toast.error(message);
      setDeleteOpen(false);
    }
  };

  // const openApproveDialog = (req) => {
  //   if (!canApproveOrReject) return toast.error("You don't have permission to approve requests");
  //   setCurrentReq(req);
  //   const initial = {};
  //   req.items.forEach(item => {
  //     initial[item.id] = item.request_quantity;
  //   });
  //   setApproveQuantities(initial);
  //   setApproveOpen(true);
  // };

  // const openApproveDialog = async (req) => {
  //   if (!canApproveOrReject) return toast.error("You don't have permission to approve requests");
  //   setCurrentReq(req);

  //   // Reset states
  //   setApproveQuantities({});
  //   setApproveSelectedInventories({});
  //   setAvailableInventories({});
  //   setLoadingAvailable(true);

  //   try {
  //     const response = await apiClient.get(`/requests/${req.id}/available-inventories/`);
  //     setAvailableInventories(response.data);

  //     // Initialize quantities for Non-IT
  //     const initialQty = {};
  //     req.items.forEach(item => {
  //       if (!item.inventory_details?.is_it) {
  //         initialQty[item.id] = item.request_quantity;
  //       }
  //     });
  //     setApproveQuantities(initialQty);

  //     // Initialize selected inventories for IT
  //     const initialSelected = {};
  //     req.items.forEach(item => {
  //       if (item.inventory_details?.is_it) {
  //         initialSelected[item.id] = []; // empty array initially
  //       }
  //     });
  //     setApproveSelectedInventories(initialSelected);
  //   } catch (err) {
  //     toast.error('Failed to load available items');
  //     console.error(err);
  //   } finally {
  //     setLoadingAvailable(false);
  //   }

  //   setApproveOpen(true);
  // };


  // In RequestIssuancePage.jsx - Replace openApproveDialog

  const availableInventories = useSelector(
    state => state.common.availableInventories.byRequest[currentReq?.id] || {}
  );
  const loadingAvailable = useSelector(state => state.common.availableInventories.loading);

  // const openApproveDialog = async (req) => {
  //   if (!canApproveOrReject) return toast.error("You don't have permission to approve requests");

  //   setCurrentReq(req);
  //   setApproveQuantities({});
  //   setApproveSelectedInventories({});

  //   // Dispatch the existing thunk
  //   dispatch(fetchAvailableInventories(req.id));

  //   // Initialize quantities for Non-IT items
  //   const initialQty = {};
  //   req.items.forEach(item => {
  //     if (!item.inventory_details?.is_it) {
  //       initialQty[item.id] = item.request_quantity;
  //     }
  //   });
  //   setApproveQuantities(initialQty);

  //   // Initialize empty selection for IT items
  //   const initialSelected = {};
  //   req.items.forEach(item => {
  //     if (item.inventory_details?.is_it) {
  //       initialSelected[item.id] = [];
  //     }
  //   });
  //   setApproveSelectedInventories(initialSelected);

  //   setApproveOpen(true);
  // };

  const openApproveDialog = (req) => {
    if (!canApproveOrReject) return toast.error("You don't have permission to approve requests");

    setCurrentReq(req);
    setApproveQuantities({});
    setApproveSelectedInventories({});

    // Dispatch the proper thunk - this stores data in Redux
    dispatch(fetchAvailableInventories(req.id));

    // Initialize Non-IT quantities
    const initialQty = {};
    req.items.forEach(item => {
      if (!item.inventory_details?.is_it) {
        initialQty[item.id] = item.request_quantity;
      }
    });
    setApproveQuantities(initialQty);

    // Initialize IT selections
    const initialSelected = {};
    req.items.forEach(item => {
      if (item.inventory_details?.is_it) {
        initialSelected[item.id] = [];
      }
    });
    setApproveSelectedInventories(initialSelected);

    setApproveOpen(true);
  };

  const toggleInventorySelection = (requestItemId, inventoryId) => {
    setApproveSelectedInventories(prev => ({
      ...prev,
      [requestItemId]: prev[requestItemId].includes(inventoryId)
        ? prev[requestItemId].filter(id => id !== inventoryId)
        : [...prev[requestItemId], inventoryId]
    }));
  };

  // const handleApprove = async () => {
  //   const items = Object.entries(approveQuantities).map(([id, qty]) => ({
  //     id,
  //     assign_quantity: Number(qty || 0),
  //   }));

  //   if (items.some(i => i.assign_quantity < 1)) {
  //     toast.error('Approved quantity must be at least 1');
  //     return;
  //   }

  //   try {
  //     await dispatch(approveRequest({ id: currentReq.id, items })).unwrap();
  //     toast.success('Request approved successfully');
  //     setApproveOpen(false);
  //     setApproveQuantities({});
  //     refetch();
  //   } catch (err) {
  //     toast.error('Failed to approve request');
  //   }
  // };

  const handleApprove = async () => {
    let hasError = false;

    const items = currentReq.items.map(item => {
      const isIT = item.inventory_details?.is_it === true;

      if (isIT) {
        const selected = approveSelectedInventories[item.id] || [];
        if (selected.length === 0) {
          toast.error(`Please select at least one physical item for "${item.inventory_details?.sub_category_name || 'IT Item'}"`);
          hasError = true;
          return null;
        }
        return {
          id: item.id,
          assigned_inventories: selected,
        };
      } else {
        const qty = Number(approveQuantities[item.id] || 0);
        if (qty < 1 || qty > item.request_quantity) {
          toast.error(`Invalid quantity for "${item.inventory_details?.sub_category_name || 'Non-IT Item'}"`);
          hasError = true;
          return null;
        }
        return {
          id: item.id,
          assign_quantity: qty,
        };
      }
    });

    if (hasError) return;

    try {
      await dispatch(approveRequest({ id: currentReq.id, items })).unwrap();
      toast.success('Request approved successfully');
      setApproveOpen(false);
      setApproveQuantities({});
      setApproveSelectedInventories({});
      refetch();
    } catch (err) {
      const errorMessage = typeof err === 'string' ? err : err?.message || 'Failed to approve request';

      console.error("Approval failed:", errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    try {
      await dispatch(rejectRequest({ id: currentReq.id, reason: rejectReason.trim() })).unwrap();
      toast.success('Request rejected');
      setRejectOpen(false);
      setRejectReason('');
      refetch();
    } catch (err) {
      toast.error('Failed to reject request');
    }
  };

  const currentEmployees = isITRelated ? issuanceEmployeesIT.items : issuanceEmployeesNonIT.items;

  // Filter Categories based on IT Toggle
  const filteredCategories = inventoryCategories.filter(cat => cat.is_it === isITRelated);

  const columns = [
    { accessorKey: 'code', header: 'Code' },
    {
      accessorKey: 'request_by_details',
      header: 'Requested By',
      cell: ({ row }) => row.original.request_by_details?.name || '-',
    },
    {
      accessorKey: 'request_to_details',
      header: 'Request To',
      cell: ({ row }) => row.original.request_to_details?.name || '-',
    },
    { accessorKey: 'reason', header: 'Reason' },
    {
      header: 'Items',
      cell: ({ row }) => {
        const items = row.original.items || [];
        if (items.length === 0) return <div className="text-muted-foreground text-sm italic">No items requested</div>;

        return (
          <div className="space-y-2.5 min-w-[240px]">
            {items.map((item, idx) => {
              const inv = item.inventory_details || {};
              const isOther = item.is_other;
              const displayName = isOther ? item.other_reason?.trim() : inv.name || inv.sub_category_name || 'Unnamed item';
              const label = isOther ? 'Other reason:' : 'Item:';

              return (
                <div
                  key={idx}
                  className={cn(
                    "p-3 rounded-lg border bg-muted/30 text-sm shadow-sm",
                    isOther && "border-amber-400/50 bg-amber-50/40 dark:bg-amber-950/30"
                  )}
                >
                  <div className="font-medium text-foreground/90 flex items-baseline gap-1.5">
                    <span className="font-semibold whitespace-nowrap">{label}</span>
                    <span className="leading-tight">{displayName}</span>
                  </div>
                  <div className="mt-1.5 text-xs flex items-center gap-1.5 text-muted-foreground">
                    <span className="font-medium text-foreground/80">Requested:</span>
                    <span className="font-semibold text-foreground">{item.request_quantity}</span>
                    {item.assign_quantity > 0 && (
                      <>
                        <span className="text-green-600 dark:text-green-400 font-medium">→ Assigned:</span>
                        <span className="font-semibold text-green-700 dark:text-green-300">{item.assign_quantity}</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      accessorKey: 'status_display',
      header: 'Status',
      cell: ({ row }) => (
        <Badge
          variant={
            row.original.status_display === 'approved' ? 'success' :
              row.original.status_display === 'rejected' ? 'destructive' :
                row.original.status_display === 'partially_completed' ? 'warning' : 'secondary'
          }
        >
          {row.original.status_display}
        </Badge>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created On',
      cell: ({ row }) => <span className="text-sm">{formatDateTime(row.original.created_at) ?? '-'}</span>,
    },
    //  {
    //   accessorKey: 'updated_at',
    //   header: 'Updated On',
    //   cell: ({ row }) => <span className="text-xs">{formatDateTime(row.original.updated_at) ?? '-'}</span>,
    // },
    // {
    //   accessorKey: 'created_by_name',
    //   header: 'Created By',
    //   cell: ({ row }) => <span className="text-xs">{row.original.created_by_name ?? '-'}</span>,
    // },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const r = row.original;
        // const isPending = r.status === 'pending' || r.status === 'reopened';
        const isPending = r.status === 'pending';
        const isInProgress = r.status === 'in_progress';
        const isAckPending = r.status === "ack_pending";
        const isRejected = r.status === 'rejected';
        const isNotAccepted = r.status === "not_accepted";
        // const isCompleted = ['approved', 'rejected', 'closed', 'resolved'].includes(r.status); // Adjust statuses as per your backend
        const isCompleted = r.status === 'completed';

        const canEdit = isPending && canUpdate;
        const canDeleteThis = isPending && canDelete;
        const canMarkInProgress = isPending && canApproveOrReject;
        const canApproveReject = isInProgress && canApproveOrReject;
        const needsAck = needsAcknowledgement(r);
        const isEscalated = r.status === 'escalated';
        const hideInProgress = !!r.expected_completion_datetime; // true if has data → hide
        const showInProgressForEscalated = isEscalated && !hideInProgress;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* Edit - Only pending */}
              {(canEdit || isSuperAdmin) && isPending && (
                <DropdownMenuItem onClick={() => openDialog(r)}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
              )}

              {/* Delete - Only pending */}
              {(canDeleteThis || isSuperAdmin) && (
                <DropdownMenuItem
                  onClick={() => {
                    setCurrentReq(r);
                    setDeleteOpen(true);
                  }}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              )}

              {(canUpdate || isSuperAdmin) && (
                <DropdownMenuItem onClick={() => openHistory(r)}>
                  <History className="mr-2 h-4 w-4" /> Request History
                </DropdownMenuItem>
              )}

              {/* In Progress - Only pending */}
              {(canMarkInProgress || isSuperAdmin) &&
                isPending &&
                (

                  <DropdownMenuItem
                    onClick={() => {
                      setCurrentReq(r);
                      setInProgressOpen(true);
                    }}
                    className="text-blue-600 font-medium"
                  >
                    <PlayCircle className="mr-2 h-4 w-4" /> In Progress
                  </DropdownMenuItem>
                )}

              {/* Approve / Reject - Only in_progress */}
              {canApproveReject && canUpdate &&

                <DropdownMenuItem
                  onClick={() => openApproveDialog(r)}
                  className="text-green-600 font-medium"
                >
                  <CheckCircle className="mr-2 h-4 w-4" /> Approve
                </DropdownMenuItem>
              }
              {(canUpdate || isSuperAdmin) && !isRequester(r) && !isRejected && !isCompleted && !isAckPending &&
                <DropdownMenuItem
                  onClick={() => {
                    setCurrentReq(r);
                    setRejectOpen(true);
                  }}
                  className="text-destructive"
                >
                  <XCircle className="mr-2 h-4 w-4" /> Reject
                </DropdownMenuItem>
              }


              {/* Acknowledge Receipt */}
              {needsAck && (
                <DropdownMenuItem
                  onClick={() =>
                    dispatch(
                      setPendingRequestAck({
                        id: r.id,
                        data: r,
                      })
                    )
                  }
                  className="text-green-600 font-medium"
                >
                  <CheckCircle className="mr-2 h-4 w-4" /> Acknowledge Receipt
                </DropdownMenuItem>
              )}

              {/* Fallback */}
              {!(
                canEdit ||
                canDeleteThis ||
                canMarkInProgress ||
                canApproveReject ||
                needsAck ||
                canUpdate
              ) && (
                  <DropdownMenuItem disabled className="text-muted-foreground">
                    No actions available
                  </DropdownMenuItem>
                )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Request Issuance"
        description="Manage item issuance requests"
        action={canCreate ? { label: 'New Request', onClick: () => openDialog() } : null}
      />

      {/* {isLoading && !requests.length && (
        <div className="flex justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading requests...
        </div>
      )} */}


      <DataTable
        data={requests}
        totalCount={pagination.count}
        columns={columns}
        searchPlaceholder="Search by code, reason, or employee..."
        showExport={false}
        showImport={false}
        pageIndex={page - 1}
        pageSize={pageSize}
        pageCount={Math.ceil(pagination.count / pagination.page_size)}
        onPageChange={(p) => setPage(p + 1)}
        onPageSizeChange={setPageSize}
        onSearch={setSearchTerm}
        isLoading={isLoading}
        onDelete={async (id) => {
          await dispatch(deleteRequest(id));
          refetch();
        }}
        onRestore={async (id) => {
          await dispatch(restoreRole({ entityType: 'requests', id }));
          refetch();
        }}
      />

      {/* CREATE / EDIT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-screen overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{currentReq ? 'Edit Request' : 'New Issuance Request'}</DialogTitle>
            <DialogDescription>
              {currentReq ? 'Update request details' : 'Request items from inventory'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-muted/50 rounded-lg">
              <Label className="text-base font-medium">Request Type</Label>
              <div className="flex items-center gap-4">
                <span className={!isITRelated ? "font-medium" : "text-muted-foreground"}>Non-IT</span>
                <Switch checked={isITRelated} onCheckedChange={(checked) => {
                  setIsITRelated(checked);
                  // Clear item categories/stock when type changes
                  setForm(p => ({
                    ...p,
                    items: p.items.map(i => ({ ...i, category: '', inventory: '' }))
                  }));
                }} />
                <span className={isITRelated ? "font-medium text-primary" : "text-muted-foreground"}>IT Related</span>
              </div>
            </div>

            <FormField
              label="Request To"
              type="select"
              value={form.request_to}
              onChange={(e) => setForm(p => ({ ...p, request_to: e.target.value }))}
              // options={employees.map(e => ({ value: e.id, label: `${e.first_name} ${e.last_name || ''} (${e.code})` }))}
              options={currentEmployees.map(e => ({
                value: e.id,
                label: e.name // ← Now uses `name` from dropdown API
              }))}
              error={formErrors.request_to}
              required
            />

            {canApproveOrReject &&
              <FormField
                label="Behalf Of"
                type="select"
                value={form.behalf_of}
                onChange={(e) => setForm(p => ({ ...p, behalf_of: e.target.value }))}
                options={BEHALF_OF_OPTIONS}
              />
            }

            {(form.behalf_of === 'new_joined' || form.behalf_of === 'management') && (

              <FormField
                label="Behalf of Employee"
                type="select"
                value={form.behalf_of_employee}
                onChange={(e) => setForm(p => ({ ...p, behalf_of_employee: e.target.value }))}
                // options={employees.map(e => ({ value: e.id, label: `${e.first_name} ${e.last_name || ''} (${e.code})` }))}
                options={employees.map(e => ({
                  value: e.id,
                  label: e.name // ← Now uses `name` from dropdown API
                }))}
                error={formErrors.behalf_of_employee}
                required
              />
            )}

            <FormField
              label="Reason / Purpose"
              type="textarea"
              value={form.reason}
              onChange={(e) => setForm(p => ({ ...p, reason: e.target.value }))}
              error={formErrors.reason}
              placeholder="Why do you need these items?"
              required
            />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">Requested Items</h4>
                <Button size="sm" onClick={addItemRow}>
                  <Plus className="mr-2 h-4 w-4" /> Add Item
                </Button>
              </div>

              {formErrors.items && (
                <p className="text-sm text-destructive">{formErrors.items}</p>
              )}

              {form.items.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground border-2 border-dashed rounded-lg">
                  No items added. Click "Add Item" to begin.
                </div>
              ) : (
                <div className="space-y-6">
                  {form.items.map((item, idx) => {
                    // Get stock items based on selected Category ID
                    const stockData = item.category
                      ? (isITRelated
                        ? (itStockByCategory[item.category] || { items: [] })
                        : (nonITStockByCategory[item.category] || { items: [] })
                      )
                      : { items: [] };

                    const stockItems = stockData.items || [];

                    return (
                      <div key={idx} className="space-y-4 p-5 border rounded-lg bg-muted/20">

                        {/* NEW: Category Dropdown */}
                        {!item.is_other && (
                          <FormField
                            label="Category"
                            type="select"
                            placeholder="Select category..."
                            value={item.category}
                            onChange={(e) => updateItem(idx, 'category', e.target.value)}
                            options={filteredCategories.map(c => ({ value: c.id, label: c.name }))}
                            error={formErrors[`item_${idx}_category`]}
                            disabled={item.is_other}
                          />
                        )}

                        <FormField
                          label="Item from Inventory"
                          type="select"
                          placeholder="Select item..."
                          value={item.inventory}
                          onChange={(e) => updateItem(idx, 'inventory', e.target.value)}
                          options={stockItems.map(s => ({
                            value: s.id,
                            label: `${s.sub_category_name}`,
                          }))}
                          error={formErrors[`item_${idx}_inventory`]}
                          disabled={item.is_other || !item.category}
                        />

                        <FormField
                          label="Is this a non-inventory item?"
                          type="switch"
                          checked={item.is_other}
                          onCheckedChange={(c) => {
                            updateItem(idx, 'is_other', c);
                            if (c) updateItem(idx, 'inventory', '');
                            if (c) updateItem(idx, 'category', '');
                          }}
                        />

                        {item.is_other && (
                          <FormField
                            label="Describe the Item"
                            placeholder="e.g., Ergonomic Chair, HDMI Cable..."
                            value={item.other_reason}
                            onChange={(e) => updateItem(idx, 'other_reason', e.target.value)}
                            error={formErrors[`item_${idx}_other_reason`]}
                            required
                          />
                        )}

                        <FormField
                          label="Quantity"
                          type="text"
                          inputMode="numeric"
                          maxLength={10}
                          value={item.request_quantity}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                            updateItem(idx, 'request_quantity', value);
                            if (value && Number(value) === 0) {
                              setFormErrors(prev => ({ ...prev, [`item_${idx}_quantity`]: 'Quantity must be at least 1' }));
                            } else {
                              setFormErrors(prev => ({ ...prev, [`item_${idx}_quantity`]: undefined }));
                            }
                          }}
                          error={formErrors[`item_${idx}_quantity`]}
                          required
                          placeholder="Enter quantity"
                        />

                        <div className="flex justify-end">
                          <Button variant="destructive" size="sm" onClick={() => removeItemRow(idx)}>
                            <Trash2 className="mr-2 h-4 w-4" /> Remove
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmit}>
                {currentReq ? 'Update Request' : 'Submit Request'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* APPROVE DIALOG */}
      {/* <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Approve Request #{currentReq?.code}</DialogTitle>
            <DialogDescription>Set approved quantity for each item</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {currentReq?.items.map((item) => {
              const requested = item.request_quantity;
              const approved = approveQuantities[item.id] ?? requested;
              const itemName = item.is_other ? item.other_reason : item.inventory_details?.sub_category_name || 'Unknown Item';

              return (
                <div key={item.id} className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                  <div className="flex-1">
                    <p className="font-medium">{itemName}</p>
                    <p className="text-sm text-muted-foreground">Requested: <strong>{requested}</strong></p>
                  </div>
                  <div className="w-32">
                    <FormField
                      type="number"
                      value={approved}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || (Number(val) >= 0 && Number(val) <= requested)) {
                          setApproveQuantities(p => ({ ...p, [item.id]: val }));
                        }
                      }}
                      min="0"
                      max={requested}
                      className="text-center"
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button onClick={handleApprove} className="bg-green-600 hover:bg-green-700">
              Approve Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog> */}
      {/* APPROVE DIALOG */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Approve Request #{currentReq?.code}</DialogTitle>
            <DialogDescription>Assign items for approval</DialogDescription>
          </DialogHeader>

          {loadingAvailable ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mb-4" />
              <p>Loading available items...</p>
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {currentReq?.items.map((item) => {
                const isIT = item.inventory_details?.is_it === true;
                const requested = item.request_quantity;
                const itemName = item.is_other
                  ? item.other_reason
                  : item.inventory_details?.name || 'Unknown Item';

                // CORRECT KEY: subcategory ID from inventory_details
                const subcategoryId = item.inventory_details?.id;
                const availableList = subcategoryId ? (availableInventories[subcategoryId] || []) : [];

                const selectedCount = approveSelectedInventories[item.id]?.length || 0;

                return (
                  <Card key={item.id} className="p-6">
                    <div className="mb-4">
                      <p className="text-lg font-semibold">{itemName}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <p className="text-sm text-muted-foreground">
                          Requested: <strong>{requested}</strong>
                        </p>
                        <Badge variant={isIT ? 'default' : 'secondary'}>
                          {isIT ? 'IT' : 'Non-IT'}
                        </Badge>
                      </div>
                    </div>

                    {isIT ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Select Items to Assign</Label>
                          <span className="text-sm font-medium text-primary">
                            {selectedCount}/{requested} selected
                          </span>
                        </div>

                        {availableList.length === 0 ? (
                          <p className="text-sm text-orange-600 bg-orange-50 p-4 rounded-lg">
                            No available items in stock for this category
                          </p>
                        ) : (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-between">
                                {selectedCount === 0
                                  ? "Select items..."
                                  : `${selectedCount} item${selectedCount > 1 ? 's' : ''} selected`}
                                <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-full p-0" align="start">
                              <Command>
                                <CommandInput placeholder="Search by serial, model, brand..." />
                                <CommandList>
                                  <CommandEmpty>No items found.</CommandEmpty>
                                  <CommandGroup heading={`${availableList.length} available`}>
                                    {availableList.map((inv) => {
                                      const isSelected = approveSelectedInventories[item.id]?.includes(inv.id);
                                      return (
                                        <CommandItem
                                          key={inv.id}
                                          onSelect={() => toggleInventorySelection(item.id, inv.id)}
                                        >
                                          <div className={cn(
                                            "mr-2 h-4 w-4 rounded border flex items-center justify-center",
                                            isSelected ? "bg-primary border-primary" : "border-muted-foreground"
                                          )}>
                                            {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                                          </div>
                                          <div className="flex-1">
                                            <p className="font-medium">
                                              {inv.serial_no || 'No Serial'}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                              {inv.model_no} • {inv.brand_name}
                                              {inv.warranty_expired_on && ` • Exp: ${format(parseISO(inv.warranty_expired_on), 'dd MMM yyyy')}`}
                                            </p>
                                          </div>
                                        </CommandItem>
                                      );
                                    })}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        )}

                        {selectedCount > requested && (
                          <p className="text-sm text-destructive mt-2">
                            You cannot select more than requested quantity ({requested})
                          </p>
                        )}
                      </div>
                    ) : (
                      // Non-IT: Quantity input
                      <div className="space-y-2">
                        <Label>Approve Quantity (max {requested})</Label>
                        <input
                          type="number"
                          min="0"
                          max={requested}
                          value={approveQuantities[item.id] ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || (Number(val) >= 0 && Number(val) <= requested)) {
                              setApproveQuantities(p => ({ ...p, [item.id]: val }));
                            }
                          }}
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              disabled={loadingAvailable}
              className="bg-green-600 hover:bg-green-700"
            >
              Confirm Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MARK AS IN PROGRESS WITH EXPECTED COMPLETION TIME */}
      <AlertDialog open={inProgressOpen} onOpenChange={setInProgressOpen}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Request as In Progress</AlertDialogTitle>
            <AlertDialogDescription>
              This will notify the requester that work has started.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="expected-completion">
                Expected Completion Time <span className="text-red-500">*</span>
              </Label>
              <input
                type="datetime-local"
                id="expected-completion"
                value={expectedCompletion}
                onChange={(e) => {
                  setExpectedCompletion(e.target.value);
                  setExpectedCompletionError(''); // clear error on change
                }}
                min={new Date().toISOString().slice(0, 16)}
                className={`w-full mt-2 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary
    ${expectedCompletionError ? 'border-red-500 focus:ring-red-500' : ''}
  `}
              />
              {expectedCompletionError && (
                <p className="text-sm text-red-500 mt-1">
                  {expectedCompletionError}
                </p>
              )}


            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              type="button"
              className="bg-blue-600 hover:bg-blue-700"
              onClick={async () => {
                if (!expectedCompletion) {
                  setExpectedCompletionError('Date and time is required');
                  return; // ✅ modal stays open
                }

                try {
                  await dispatch(markInProgress({
                    id: currentReq.id,
                    expected_completion_datetime: expectedCompletion,
                  })).unwrap();

                  toast.success('Request marked as In Progress');
                  setExpectedCompletion('');
                  setExpectedCompletionError('');
                  setInProgressOpen(false); // ✅ close only on success
                  refetch();
                } catch (err) {
                  toast.error(err?.detail || 'Failed to update status');
                }
              }}
            >
              <PlayCircle className="mr-2 h-4 w-4" />
              Confirm & Start
            </Button>


          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* DELETE & REJECT */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Request?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Reject Request</AlertDialogTitle></AlertDialogHeader>
          <FormField
            label="Reason for Rejection"
            type="textarea"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Explain why this request is rejected..."
            required
          />
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReject} className="bg-destructive">Reject Request</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* REQUEST ACKNOWLEDGEMENT MODAL */}
      <Dialog

        open={!!pendingAcknowledgement?.open}
        onOpenChange={(open) => {
          if (!open) {
            dispatch(clearPendingRequestAck());
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-green-500" />
              Confirm Item Receipt
            </DialogTitle>
            <DialogDescription>
              Request <strong>#{pendingAcknowledgement?.request?.code}</strong> has been approved.
              Please confirm you have received the items.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="font-medium">Approved Items</Label>
              <div className="mt-2 space-y-2">
                {pendingAcknowledgement?.request?.items?.map((item) => (
                  <div key={item.id} className="flex justify-between p-3 bg-muted rounded-lg text-sm">
                    <span>
                      {item.is_other
                        ? item.other_reason
                        : item.inventory_details?.name || "Unknown Item"
                      }
                    </span>
                    <Badge variant="secondary">
                      {item.assign_quantity > 0 ? item.assign_quantity : item.request_quantity}x
                    </Badge>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Notes (optional for accept, required for reject)</Label>
              <Textarea
                placeholder="e.g., All items received in good condition..."
                value={ackNotes}
                onChange={(e) => setAckNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={async () => {
                if (!ackNotes.trim()) return toast.error("Please provide a reason for rejection");
                try {
                  await dispatch(
                    acknowledgeRequest({
                      id: pendingAcknowledgement.requestId,  // ← now guaranteed correct
                      is_accepted: false,
                      notes: ackNotes.trim(),
                    })
                  ).unwrap();
                  toast.success("Request rejected & reopened");
                  dispatch(clearPendingRequestAck());
                  setAckNotes("");
                } catch {
                  toast.error("Failed to reject");
                }
              }}
            >
              Reject & Reopen
            </Button>

            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={async () => {
                try {
                  await dispatch(
                    acknowledgeRequest({
                      id: pendingAcknowledgement.requestId,  // ← correct ID
                      is_accepted: true,
                      notes: ackNotes.trim() || "Items received in good condition",
                    })
                  ).unwrap();
                  toast.success("Items acknowledged successfully");
                  dispatch(clearPendingRequestAck());
                  setAckNotes("");
                } catch {
                  toast.error("Failed to acknowledge");
                }
              }}
            >
              Accept & Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REQUEST HISTORY DIALOG – USING YOUR EXISTING LogDataDisplay COMPONENT */}
    <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-xl">Request History</DialogTitle>
            <DialogDescription>
              Audit trail for request: <strong className="text-primary">
                #{selectedRequestId && requestLogs.find(l => l.object_id === selectedRequestId)?.object_model || 'Loading...'}
              </strong>
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[65vh] pr-4">
            {logsLoading ? (
              <div className="text-center py-16 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3" />
                <p>Loading history...</p>
              </div>
            ) : requestLogs.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No activity recorded for this request</p>
              </div>
            ) : (
              <div className="space-y-6 py-4">
                {requestLogs
                  .filter(log => log.object_id === selectedRequestId && log.resource === 'request' && !(
                        // log.action_type === "auto_escalate" || 
                        log.data?.changes?.escalation_cycle
                      ))
                  .map((log) => {
                    const by = log.created_by
                      ? `${log.created_by.first_name || ''} ${log.created_by.last_name || ''}`.trim() || log.created_by.code || 'Unknown'
                      : 'System';

                    return (
                      <Card key={log.id} className="p-6 hover:shadow-md transition-all border">
                        <div className="gap-4">
                          {/* Timeline */}
                          <div className="text-start text-xs text-muted-foreground shrink-0 mb-2">
                            {/* <p>{formatDateTime(log.created_at)}</p> */}
                            <p className="font-medium">{formatDateTime(log.created_at)}</p>
                          </div>

                          <div className="flex-1 space-y-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-semibold text-lg">{log.action_display || log.action_type}</p>
                                <p className="text-sm text-muted-foreground">by <strong>{by}</strong></p>
                                {log.created_by?.code && (
                                  <p className="text-xs text-muted-foreground">({log.created_by.code})</p>
                                )}
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge variant={
                                  log.action_type === 'create' ? 'success' :
                                    log.action_type === 'update' ? 'default' :
                                      log.action_type === 'auto_escalate' ? 'warning' :
                                        'destructive'
                                }>
                                  {log.action_type.replace('_', ' ').toUpperCase()}
                                </Badge>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedLog(log);
                                    setDetailsOpen(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4 mr-1" /> View Details
                                </Button>
                              </div>
                            </div>

                            {/* Summary */}
                            {/* {log.summary && (
                              <p className="text-foreground">{log.summary}</p>
                            )} */}
                          </div>
                        </div>
                      </Card>
                    );
                  })}
              </div>
            )}
          </ScrollArea>

          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* DETAILED LOG MODAL – USING YOUR LogDataDisplay COMPONENT */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh]">
          <DialogHeader>
            <DialogTitle className="text-2xl">Activity Details</DialogTitle>
            <DialogDescription>
              Full audit entry • {selectedLog && formatDateTime(selectedLog.created_at)}
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <ScrollArea className="max-h-[70vh] pr-4">
              <div className="space-y-8 py-6">

                {/* Header Summary */}
                <div className="bg-card border rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Performed by</p>
                      <p className="text-xl font-bold">
                        {selectedLog.created_by
                          ? `${selectedLog.created_by.first_name || ''} ${selectedLog.created_by.last_name || ''}`.trim() || selectedLog.created_by.code
                          : 'System'
                        }
                      </p>
                      {selectedLog.created_by?.code && (
                        <p className="text-sm text-muted-foreground">({selectedLog.created_by.code})</p>
                      )}
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-lg px-4 py-2">
                        {selectedLog.resource_display || selectedLog.resource}
                      </Badge>
                      <div className="mt-2">
                        <Badge variant={
                          selectedLog.action_type === 'create' ? 'success' :
                            selectedLog.action_type === 'update' ? 'default' :
                              selectedLog.action_type === 'auto_escalate' ? 'warning' :
                                'destructive'
                        } className="text-lg px-4 py-2">
                          {selectedLog.action_display || selectedLog.action_type.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* <div className="pt-4 border-t">
              <p className="text-sm font-medium text-muted-foreground">Summary</p>
              <p className="text-lg mt-2 font-medium text-foreground">
                {selectedLog.summary || 'No summary available'}
              </p>
            </div> */}
                </div>

                {/* AUTO ESCALATE SPECIAL DISPLAY – FIXED (no formatValue dependency) */}
                {selectedLog.action_type === 'auto_escalate' && selectedLog.data && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Escalation Details</h3>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 space-y-3 text-sm">
                      {Object.entries(selectedLog.data)
                        .filter(([key]) => !['changes', 'old', 'new'].includes(key))
                        .map(([key, value]) => {
                          // Manual formatting for common types (no formatValue needed)
                          let displayValue = value;
                          if (value === null || value === undefined) {
                            displayValue = 'None';
                          } else if (Array.isArray(value)) {
                            displayValue = value.join(', ');
                          } else if (typeof value === 'string') {
                            // Simple datetime check and format
                            const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
                            if (isoDateRegex.test(value)) {
                              displayValue = format(parseISO(value), "dd MMM yyyy, hh:mm a");
                            }
                          }

                          return (
                            <div key={key} className="flex justify-between">
                              <span className="font-medium capitalize text-muted-foreground">
                                {key.replace(/_/g, ' ')}
                              </span>
                              <span className="font-medium">
                                {displayValue}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* MAIN LOG DATA – USING YOUR REUSABLE COMPONENT */}
                <LogDataDisplay logData={selectedLog.data} />

              </div>
            </ScrollArea>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequestIssuancePage;